import { ConsentVariant } from "../../utils/partnerConsent";

interface CopyContext {
  variant: ConsentVariant;
  /** The partner's display name, e.g. "Banco del Sol". */
  partner: string;
  /** The company opening the account, e.g. "Allaria Fondos". */
  host: string;
  /** Where the error screens send people who are stuck. */
  supportEmail: string;
}

/**
 * Every string the flow renders. A function of variant and partner name rather
 * than a file per partner: the wording is the same for every bank, with their
 * name in it, so a new one reuses all of this.
 */
export const getConsentCopy = ({
  variant,
  partner,
  host,
  supportEmail,
}: CopyContext) => {
  const isLinking = variant === "VINCULACION";
  const isInvitation = variant === "INVITACION";
  const isConformity = variant === "CONFORMIDAD";
  // Both open from an ALyC's data and share everything but the consent itself.
  const fromAlyc = isInvitation || isConformity;
  // Lets the error copy say "esta apertura" / "esta vinculación" unbranched.
  const process = isLinking ? "vinculación" : "apertura";

  return {
    welcome: {
      greeting: (name: string) => (name ? `¡Hola, ${name}!` : "¡Hola!"),
      /** ALyC variants only: the account being handed over, under the greeting. */
      account: (holder: string, comitente: string) =>
        [holder, comitente].filter(Boolean).join(" - "),
      /** Page-level, under the greeting, and the same for every variant. */
      subtitle: `Te damos la bienvenida a ${host}`,
      // No DNI by design: it says which partner the data came from, not which
      // document. Nothing else on either screen shows one either.
      alert: isConformity
        ? `Con tu conformidad, vamos a abrir tu cuenta con los datos que tenés en ${partner}.`
        : variant === "APERTURA" &&
          `Usamos los datos que compartiste con ${partner} para completar tu apertura.`,
      /** Panel title: what the screen asks, as opposed to the greeting above. */
      lead: isConformity
        ? "Aceptá los siguientes puntos para continuar:"
        : isInvitation
          ? "Para seguir con la apertura de la cuenta FAL de tu empresa, necesitamos tu conformidad."
          : isLinking
            ? `Vinculá tu cuenta de ${partner} con tu cuenta comitente de Allaria.`
            : "Para abrir tu cuenta, aceptá los términos y condiciones.",
      /**
       * Linking's consent, one entry per paragraph — HTML would collapse the
       * newlines of a single string. Opening's checkboxes aren't here: they
       * are the host company's own `termsAndConditions`.
       */
      conformity: [
        `Autorizo expresamente a ALLARIA S.A. a poner a disposición de ${partner} la posición y movimientos de la cuenta comitente de mi titularidad.`,
        "Esta autorización se otorga con el propósito de dar cumplimiento a las gestiones bancarias y requerimientos normativos derivados de la relación entre las partes.",
        "La presente autorización tendrá vigencia desde la fecha de confirmación hasta que su revocación expresa sea notificada fehacientemente y por escrito a ALLARIA S.A.",
      ],
      comitenteLabel: "Seleccioná la cuenta que querés vincular",
      comitentePlaceholder: "Cuenta comitente",
      /** INVITACION's single consent. */
      termsPrefix: "Acepto los",
      terms: "Términos y condiciones",
      /** CONFORMIDAD's two consents. */
      dataTransfer: "Autorizo la cesión de mis datos",
      managementRegulationPrefix: "Acepto el",
      managementRegulation: "Reglamento de gestión",
      /** Tag beside the label on a checkbox that can be left unticked. */
      optional: "Opcional",
      submit: fromAlyc
        ? "Dar conformidad"
        : isLinking
          ? "Solicitar vinculación de cuenta"
          : "Solicitar apertura de cuenta",
    },

    // Linking only, so nothing here branches on `isLinking`.
    preconfirmation: {
      title: "Preconfirmación de vinculación",
      subtitle: "Revisá que los datos sean correctos",
      accountTitle: "Cuenta a vincular",
      cuit: "CUIT / CUIL",
      firstName: "Nombre/s",
      lastName: "Apellido/s",
      accountNumber: "Comitente",
      back: "Volver",
      confirm: "Confirmar solicitud de vinculación",
    },

    // Descriptions are paragraph lists: the first is the card's own, the rest
    // stack below it. An empty entry is skipped, not rendered blank.
    feedback: {
      /** Generic error only: the one failure a second attempt can clear. */
      retry: "Reintentar",

      // No redirect on the ALyC variants, so the card itself says what's next.
      success: fromAlyc
        ? {
            type: "SUCCESS" as const,
            title: "¡Listo! Ya solicitaste la apertura",
            description: [
              `Vamos a avanzar con la apertura de tu cuenta en ${host}. Ya podés cerrar esta ventana.`,
            ],
          }
        : {
            // The wait is the good outcome: the request went through.
            type: "PENDING" as const,
            title: `¡Listo! Tu ${process} está en proceso`,
            description: [],
          },
      // Where a refresh after signing lands. A success, with nothing left to do.
      alreadyAccepted: {
        title: "¡Ya completaste esta solicitud!",
        description: [],
      },
      // Another representative of the same comitente signed first.
      completed: {
        title: "Solicitud completada",
        description: (account: string) => [
          account
            ? `Ya hay una solicitud completada para la comitente ${account}`
            : "Ya hay una solicitud completada para esta comitente",
        ],
      },
      // Expired and superseded links land here too: the design merged the screens.
      invalidLink: {
        title: "Este enlace no es válido",
        description: [],
      },
      genericError: {
        title: "No pudimos procesar tu solicitud",
        description: [
          "Probá de nuevo en unos minutos.",
          `Si el error persiste, contactate con ${supportEmail}.`,
        ],
      },
    },
  };
};

export type ConsentCopy = ReturnType<typeof getConsentCopy>;
